
import React, { createContext, useContext, useState } from 'react';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';

interface FilterState {
  fromDate: string;
  toDate: string;
  location: string;
  categories: string[];
  groups?: string[];
  showSharedActivities: boolean;
}

interface SearchContextType {
  searchQuery: string;
  inputValue: string;
  setSearchQuery: (query: string) => void;
  activeFilters: FilterState;
  setActiveFilters: (filters: FilterState) => void;
  clearFilters: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedSearch(searchQuery, 300);
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    fromDate: '',
    toDate: '',
    location: '',
    categories: [],
    groups: [],
    showSharedActivities: true,
  });

  const handleSearchChange = (value: string) => {
    setInputValue(value);
    setSearchQuery(value);
  };

  const clearFilters = () => {
    setActiveFilters({
      fromDate: '',
      toDate: '',
      location: '',
      categories: [],
      groups: [],
      showSharedActivities: true,
    });
  };

  return (
    <SearchContext.Provider 
      value={{ 
        searchQuery: debouncedSearchQuery,
        inputValue,
        setSearchQuery: handleSearchChange, 
        activeFilters, 
        setActiveFilters,
        clearFilters 
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
